import { FormattedMessage } from 'react-intl';
import { DoPaylinkPaymentDocument } from '@packages/gql/generated/shopping/DoPaylinkPaymentDocument';
import { useAuthMutation } from '@packages/shared/src/hooks/useAuthMutation/useAuthMutation';
import type { PaylinkPaymentType } from '@packages/gql/generated/shopping/graphql';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { Button } from '@packages/shared/src/ui/Button/Button';

/* GraphQL */ `
  mutation DoPaylinkPayment(
    $locale: String!
    $url: String!
    $amount: Int!
    $paymentType: PaylinkPaymentType!
  ) {
    doPaylinkPayment(locale: $locale, url: $url, amount: $amount, paymentType: $paymentType) {
      redirect {
        url
      }
      error {
        errorText
        type
      }
    }
  }
`;

const isTypeCreditCard = (type: PaylinkPaymentType) =>
  type === 'AMERICANEXPRESS' || type === 'MASTERCARD' || type === 'VISA' || type === 'CREDITCARD';

export const PayButton = ({
  selectedType,
  onError,
  amount,
  url,
}: {
  selectedType?: PaylinkPaymentType;
  onError: (message: string) => void;
  amount: number;
  url: string;
}) => {
  const [paypageUrl, setPaypageUrl] = useState<string>();
  const { locale } = useI18n();
  const router = useRouter();

  const [{ fetching: paying }, doPayment] = useAuthMutation(DoPaylinkPaymentDocument);

  useEffect(() => {
    if (!selectedType) {
      // if the selected type is reset by the externalErrorCallback in the parent component, close the dialog
      setPaypageUrl(undefined);
    }
  }, [selectedType]);

  const handlePayment = async () => {
    if (selectedType) {
      const res = await doPayment({
        locale,
        url,
        amount,
        paymentType: selectedType,
      });
      if (res.data?.doPaylinkPayment) {
        const { redirect, error: payError } = res.data.doPaylinkPayment;

        if (payError && payError.errorText) {
          onError(payError.errorText);
        }

        if (redirect && redirect.url) {
          if (isTypeCreditCard(selectedType)) {
            setPaypageUrl(redirect.url);
          } else {
            router.push(redirect.url);
          }
        }
      }
    }
  };

  return (
    <>
      {paypageUrl && (
        <Dialog
          isOpen
          onClose={() => setPaypageUrl(undefined)}
          title=""
          className="md:min-w-[37rem]"
        >
          <DialogContent>
            <iframe title="Kreditkarte" width="100%" height="860px" src={paypageUrl} />
          </DialogContent>
        </Dialog>
      )}
      <Button
        color={!selectedType || paying ? 'default' : 'primary'}
        disabled={!selectedType || paying}
        onClick={handlePayment}
      >
        <FormattedMessage
          key="Paylink.prepare.wegmitderkohle-text"
          id="Paylink.prepare.wegmitderkohle"
          defaultMessage="Jetzt Beträge begleichen"
        />
      </Button>
    </>
  );
};
